import type { DbListing } from "@/hooks/useDatabase";

type PricedListing = Pick<DbListing, "id" | "price" | "total_mg" | "stock_status">;
type VendorPriority = { id: string; priority?: number | null };
type Stocked = { stock_status?: string | null };

// The one test for "can someone buy this right now": anything not marked sold out (in stock, low stock, or no status
// recorded). Every widget that picks a listing or labels a Buy button uses it, so they agree on what is buyable.
export function isBuyable(listing: Stocked | null | undefined): boolean {
  return !!listing && listing.stock_status !== "out_of_stock";
}

// The listing a price widget features: the vendor with the lowest priority number, then the lower price, picked from
// the buyable listings when there are any, so a Buy button never features a sold-out product over one that ships.
// featuredListing() below builds on it.
export function priorityListing<T extends { vendor_id: string; price: number; id?: string } & Stocked>(
  listings: T[],
  vendors: VendorPriority[],
): T | null {
  const buyable = listings.filter(isBuyable);
  const pool = buyable.length > 0 ? buyable : listings;
  if (pool.length === 0) return null;
  return pool.reduce((a, b) => {
    const aPriority = vendors.find((v) => v.id === a.vendor_id)?.priority ?? 999;
    const bPriority = vendors.find((v) => v.id === b.vendor_id)?.priority ?? 999;
    if (aPriority !== bPriority) return aPriority < bPriority ? a : b;
    return cheaper(a, b);
  }, pool[0]);
}

// The cheaper of two listings. At the same price the lower id wins, so the pick does not depend on the order the
// rows came back in (two listings of one vendor at one price used to swap between page loads).
function cheaper<T extends { price: number; id?: string }>(a: T, b: T): T {
  if (a.price !== b.price) return a.price < b.price ? a : b;
  return String(a.id ?? "") <= String(b.id ?? "") ? a : b;
}

// priorityListing, with the per-compound vendor pin (lib/vendorOverride.ts) applied first: the pinned vendor's cheapest
// buyable listing. A pinned vendor that is sold out does not take the slot while another vendor has stock. `pinned`
// tells the caller whether the pin decided the pick, so it can say "Featured Vendor" instead of claiming a low price.
// StickyBottomCTA uses it directly; the two price blocks on a compound page use it through priceBlockPick() below.
export function featuredListing<T extends { vendor_id: string; price: number; id?: string } & Stocked>(
  listings: T[],
  vendors: VendorPriority[],
  pinnedVendorId?: string | null,
): { listing: T; pinned: boolean } | null {
  const fallback = priorityListing(listings, vendors);
  if (!fallback) return null;
  const pinnedListings = pinnedVendorId ? listings.filter((l) => l.vendor_id === pinnedVendorId) : [];
  if (pinnedListings.length === 0) return { listing: fallback, pinned: false };
  const pinnedBuyable = pinnedListings.filter(isBuyable);
  if (pinnedBuyable.length === 0 && isBuyable(fallback)) return { listing: fallback, pinned: false };
  const pool = pinnedBuyable.length > 0 ? pinnedBuyable : pinnedListings;
  return { listing: pool.reduce((a, b) => cheaper(a, b), pool[0]), pinned: true };
}

// The listing a price headline shows: the lowest price per mg among buyable listings, or among every priced listing
// when none is buyable. The whole price decides only when no listing in that pool has an mg count. /compare, the
// homepage and the compound page's market figures show it as "the lowest price on file". It is a pick for display,
// not a label: whether a listing may be called the lowest is isLowestPerMg() below, which has no fallbacks.
//
// Until 2026-09-10 the sticky bar compared price per mg across in-stock listings while the card, the sidebar and the
// exit modal compared the whole price across every listing, so on 15 compounds one vendor was "Best price" in one spot
// and "Featured" in the next. Comparing per mg also keeps a small vial from being called the lowest price.
const EPSILON = 1e-9;

export function lowestPriceListing<T extends PricedListing>(listings: T[]): T | null {
  const priced = listings.filter((l) => l.price > 0);
  const inStock = priced.filter(isBuyable);
  const pool = inStock.length > 0 ? inStock : priced;
  const withMg = pool.filter((l) => l.total_mg > 0);
  const perMg = withMg.length > 0;
  const candidates = perMg ? withMg : pool;
  if (candidates.length === 0) return null;
  const value = (l: T) => (perMg ? l.price / l.total_mg : l.price);
  return candidates.reduce((a, b) => (value(b) < value(a) ? b : a), candidates[0]);
}

// The one test behind every "lowest price" style label on the site: the price block, the phone price bar and the exit
// modal (through priceBlockPick below), the compound price table's badge, /buy, the ad compare lander, the stack
// builder and the /best pages. A listing may be called the lowest only when it has the lowest price per mg among the
// listings someone can buy (not sold out) that carry a price and an mg count. Ties all count. So none of these is
// ever labelled the lowest: a pinned or priority vendor that costs more per mg, a cheaper pack of a smaller size, a
// listing with no mg count, or a sold-out listing. When no priced listing with an mg count is buyable, nothing is.
//
// Until 2026-10-24 a pack price could be labelled "Lowest price" when no buyable listing had an mg count, and a
// sold-out listing could be the lowest when nothing was in stock. The affiliate disclosure (section 3) states this rule.
function lowestPerMgPool<T extends PricedListing>(listings: T[]): { pool: T[]; low: number } | null {
  const pool = listings.filter((l) => l.price > 0 && l.total_mg > 0 && isBuyable(l));
  if (pool.length === 0) return null;
  return { pool, low: Math.min(...pool.map((l) => l.price / l.total_mg)) };
}

export function isLowestPerMg(listing: PricedListing | null | undefined, listings: PricedListing[]): boolean {
  if (!listing) return false;
  const lowest = lowestPerMgPool(listings);
  const match = lowest?.pool.find((l) => l.id === listing.id);
  return !!lowest && !!match && match.price / match.total_mg <= lowest.low + EPSILON;
}

// The ids of every listing isLowestPerMg() accepts, for a table that badges rows.
export function lowestPerMgIds(listings: PricedListing[]): string[] {
  const lowest = lowestPerMgPool(listings);
  if (!lowest) return [];
  return lowest.pool.filter((l) => l.price / l.total_mg <= lowest.low + EPSILON).map((l) => l.id);
}

// The whole-dollar lowest and highest price, and how many vendors are in that pool: buyable priced listings, or
// every priced listing when none is buyable. CompoundPage uses this for its top stats and its mobile sticky bar,
// so both agree with each other and with the per-listing lowest-price test above (which compares per mg instead).
export function priceRange<T extends PricedListing & { vendor_id: string }>(
  listings: T[],
): { lowest: number; highest: number; vendorCount: number } {
  const priced = listings.filter((l) => l.price > 0);
  const inStock = priced.filter(isBuyable);
  const pool = inStock.length > 0 ? inStock : priced;
  if (pool.length === 0) return { lowest: 0, highest: 0, vendorCount: 0 };
  return {
    lowest: Math.min(...pool.map((l) => l.price)),
    highest: Math.max(...pool.map((l) => l.price)),
    vendorCount: new Set(pool.map((l) => l.vendor_id)).size,
  };
}

// The listing a compound page's price blocks feature, and the label they may put on it. The price block
// (BestPriceStickyBar), the phone's bottom price bar (components/compound/PhonePriceBar) and the exit modal all call
// this, so they cannot feature different vendors or label the same listing differently. A desktop sidebar widget called
// it too until the 2026-10-22 rebuild moved the price block itself into the side rail. (Until 2026-10-01 that sidebar
// picked from every listing and the top block from priced, buyable listings with an mg count: on 11 compounds they
// showed two different vendors, and the sidebar said "Lowest Price per mg" over pack prices that have no mg count.)
//
// The pick comes from priced, buyable listings that carry an mg count when there are any, because the blocks show a
// price per mg. When there are none (capsules, liquids, kits), every priced listing is in the running and the blocks
// show the pack price. A sold-out listing is only returned when no priced listing is buyable.
//
// The label is "lowest-per-mg" only when isLowestPerMg() accepts the pick and no pin put it there. Anything else,
// including a pick that vendor priority or a pin chose and a pack price with no mg count, is "featured": the
// disclosure promises that a vendor put first for any reason other than price is labelled Featured.
export type PriceBlockLabel = "lowest-per-mg" | "featured";

export function priceBlockPick<T extends PricedListing & { vendor_id: string }>(
  listings: T[],
  vendors: VendorPriority[],
  pinnedVendorId?: string | null,
): { listing: T; pinned: boolean; hasMg: boolean; label: PriceBlockLabel } | null {
  const priced = listings.filter((l) => l.price > 0);
  const buyableWithMg = priced.filter((l) => isBuyable(l) && l.total_mg > 0);
  const pick = featuredListing(buyableWithMg.length > 0 ? buyableWithMg : priced, vendors, pinnedVendorId);
  if (!pick) return null;
  const hasMg = pick.listing.total_mg > 0;
  // A pin or vendor priority decides the pick, so it is not always the cheapest. Only call it the lowest when it is.
  const isLowest = !pick.pinned && isLowestPerMg(pick.listing, listings);
  return { ...pick, hasMg, label: isLowest ? "lowest-per-mg" : "featured" };
}

/** The words for a price block label. One place, so the price block, the phone bar and the exit modal agree. */
export function priceBlockLabelText(label: PriceBlockLabel): string {
  return label === "lowest-per-mg" ? "Lowest price per mg" : "Featured vendor";
}

// A listing's price per mg as text: "$4.25/mg", or "$0.117/mg" under a dollar, where the third decimal is what
// separates two vendors. Bulk powders cost a fraction of a cent per mg (a 30 g tub at $74.99 is a quarter of a
// cent), which printed as "$0.00/mg" or rounded a fifth off, so under one cent per mg the price is given per gram
// instead: "$2.50/g". Call it only for a listing with an mg count.
export function formatPricePerMg(price: number, totalMg: number): string {
  return formatPerMgValue(price / totalMg);
}

// The same, for a caller that already holds the price per mg.
export function formatPerMgValue(perMg: number): string {
  if (perMg < 0.01) return `$${(perMg * 1000).toFixed(2)}/g`;
  return `$${perMg.toFixed(perMg < 1 ? 3 : 2)}/mg`;
}

// The same figure for a table column headed "$/mg": the header carries the unit, except when the price is per gram.
export function formatPerMgCell(perMg: number): string {
  const text = formatPerMgValue(perMg);
  return text.endsWith("/mg") ? text.slice(0, -3) : text;
}
